import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Popconfirm, Space, Table, Tag, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { adminApi, type PageResult } from '../../api'
import { PageHeader, tablePagination, useServerPagination } from '../../components/ui'
import { AgentManageTabs } from './AgentManageTabs'

export type AiAgentRow = {
  id: number
  agent_code: string
  agent_name: string
  description: string
  chat_provider: string
  chat_model: string
  temperature: number
  sort_no: number
  is_active: boolean
  is_builtin?: boolean
  sub_agent_codes?: string[]
  orchestration_mode?: string
  max_sub_agent_steps?: number
}

function modelLabel(row: AiAgentRow) {
  if (row.chat_model) {
    return row.chat_provider ? `${row.chat_model}（${row.chat_provider}）` : row.chat_model
  }
  return '跟随全局'
}

export function AgentList() {
  const navigate = useNavigate()
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [total, setTotal] = useState(0)
  const [rows, setRows] = useState<AiAgentRow[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback((p: number, ps: number) => {
    setLoading(true)
    return adminApi
      .get<PageResult<AiAgentRow>>('/admin/agents', { params: { page: p, page_size: ps } })
      .then((r) => {
        setRows(r.data.items)
        setTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    void load(page, pageSize)
  }, [page, pageSize, load])

  const deleteAgent = (row: AiAgentRow) => {
    adminApi
      .delete(`/admin/agents/${row.id}`)
      .then(() => {
        message.success('已删除')
        void load(page, pageSize)
      })
      .catch((e) => message.error(e.message))
  }

  return (
    <>
      <AgentManageTabs />
      <div className="agent-list-head">
        <PageHeader
          title="智能体管理"
          description="管理各业务场景的大模型智能体：多语言提示词、模型与调试对话。内置智能体标识不可修改，可编辑提示词与模型。"
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/manage/agents/new')}>
          新建智能体
        </Button>
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={[
          {
            title: '名称',
            dataIndex: 'agent_name',
            render: (name: string, row: AiAgentRow) => (
              <Space size="small">
                <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/manage/agents/${row.id}`)}>
                  {name}
                </Button>
                {row.is_builtin ? <Tag>内置</Tag> : null}
              </Space>
            ),
          },
          { title: '标识', dataIndex: 'agent_code', width: 180, ellipsis: true },
          { title: '说明', dataIndex: 'description', ellipsis: true, render: (v: string) => v || '—' },
          {
            title: '模型',
            width: 200,
            ellipsis: true,
            render: (_: unknown, row: AiAgentRow) => modelLabel(row),
          },
          { title: '排序', dataIndex: 'sort_no', width: 72 },
          {
            title: '状态',
            dataIndex: 'is_active',
            width: 72,
            render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>),
          },
          {
            title: '操作',
            width: 160,
            render: (_: unknown, row: AiAgentRow) => (
              <Space size="small">
                <Button size="small" onClick={() => navigate(`/manage/agents/${row.id}`)}>
                  编辑
                </Button>
                <Popconfirm
                  title={
                    row.is_builtin
                      ? '删除内置智能体？业务将回退到代码默认或旧版模型配置中的提示词。'
                      : '确定删除该智能体？'
                  }
                  onConfirm={() => deleteAgent(row)}
                >
                  <Button size="small" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </>
  )
}
